import dayjs from "dayjs";

/**
 * Mapping of Unit Acronyms to their Full Names.
 * This is used for consistent labeling across the OUber functionality.
 */
export const officeAcronyms = {
  AO: "Accounting Office",
  OVCFA: "Office of the Vice Chancellor for Finance and Administration",
  FICS: "Faculty of Information and Communication Studies",
  CODTL: "Center for Open and Digital Teaching and Learning",
  FEd: "Faculty of Education",
  ICTDO: "Information & Communication Technology Development Office",
  FMDS: "Faculty of Management and Development Studies",
  BO: "Budget Office",
  OUR: "Office of the University Registrar",
  CDMO: "Campus Development and Maintenance Office",
  OVCAA: "Office of the Vice Chancellor for Academic Affairs",
  SPMO: "Supply and Property Management Office",
  CO: "Cash Office",
  OC: "Office of the Chancellor",
  HRDO: "Human Resources Development Office",
  QAO: "Quality Assurance Office",
  OPA: "Office of Public Affairs",
  Pahinungod: "Ugnayan ng Pahinungod/Oblation Corps Office",
  OGC: "Office of Gender Concerns",
  OSA: "Office of Student Affairs",
  OLC: "Office of the Legal Counsel",
  Library: "University Library",
};

/**
 * Maps a full unit name to its acronym.
 * 
 * @param {string} unitName - The full name of the unit
 * @returns {string} - The acronym (e.g., "FMDS") or the input if no match is found
 */
export const mapUnitToAcronym = (unitName) => {
  if (!unitName) return "";
  
  // Clean input
  const normalized = unitName.toString().trim().toLowerCase().replace(/\s*&\s*/g, " and ");
  
  // Exact match search
  for (const [acronym, name] of Object.entries(officeAcronyms)) {
    const normalizedAcronym = acronym.toLowerCase();
    const normalizedName = name.toLowerCase().replace(/\s*&\s*/g, " and ");

    if (normalizedName === normalized || normalizedAcronym === normalized) {
      return acronym;
    }
  }
  
  // Return original if no match (could be an acronym already)
  return unitName;
};

/**
 * Robustly parses employee info strings like "Staff - AO"
 * 
 * @param {string} fullString - The combined string
 * @param {string} type - 'position' or 'unit'
 * @returns {string} - The extracted piece
 */
export const parseEmployeeInfo = (fullString, type) => {
  if (!fullString || typeof fullString !== "string") return "";

  const [positionPart, acronymPart] = fullString
    .split(" - ")
    .map((str) => str.trim());

  if (type === "position") return positionPart || "";
  if (type === "unit") return officeAcronyms[acronymPart] || "";
  
  return "";
};

/**
 * Robustly extracts the unit acronym from a potential combined position-unit string.
 * It uses parseEmployeeInfo to attempt a name-based lookup and falls back to 
 * the extracted unit portion of the string if the lookup fails.
 * 
 * @param {string} rawUnit - The input string (e.g., "Staff - AO", "Administrative Officer - FMDS", or just "AO")
 * @returns {string} - The extracted acronym or "N/A"
 */
export const getUnitAcronym = (rawUnit) => {
  if (!rawUnit || typeof rawUnit !== 'string' || rawUnit === "N/A") return "N/A";
  
  let unitValue = rawUnit;

  // 1. Handle parentheses pattern: "Name (Unit Name)" or "Position (Unit Name)"
  const parenMatch = rawUnit.match(/\(([^)]+)\)/);
  if (parenMatch) {
    unitValue = parenMatch[1];
  } else {
    // 2. Fallback to "Position - Unit" pattern
    unitValue = parseEmployeeInfo(rawUnit, "unit") || rawUnit.split(" - ").pop();
  }
  
  // Clean up any remaining leading/trailing whitespace
  unitValue = unitValue.trim();

  // Map the resulting value back to an acronym if it's a full name
  return mapUnitToAcronym(unitValue) || "N/A";
};

/**
 * Removes parentheses and their content from a name string.
 * Example: "Bernard (ICTDO)" -> "Bernard"
 * 
 * @param {string} name - The original name string
 * @returns {string} - The cleaned name
 */
export const cleanName = (name) => {
  if (!name || typeof name !== 'string') return name;
  return name.replace(/\s*\([^)]+\)/g, "").trim();
};

/**
 * Robustly determines the best representing unit for a passenger/employee.
 * Filters for active positions and sorts by start date.
 * 
 * @param {object} passenger - The passenger or employee object
 * @returns {string} - The unit name or acronym
 */
export const getActiveEarliestUnit = (passenger) => {
  if (!passenger) return "";
  if (passenger.unit) return passenger.unit;
  if (!passenger.positions || passenger.positions.length === 0) return passenger.department || "";

  const todayStr = dayjs().format("YYYY-MM-DD");

  // Filter for active positions
  const activePositions = passenger.positions.filter(p => {
    const endDate = p.pivot?.position_end_date || p.position_end_date;
    if (!endDate) return true;
    return endDate >= todayStr;
  });

  const targetList = activePositions;

  // Sort by start date ASC
  const sorted = [...targetList].sort((a, b) => {
    const dateAStr = a.pivot?.position_start_date || a.position_start_date;
    const dateBStr = b.pivot?.position_start_date || b.position_start_date;
    const dateA = dateAStr ? dayjs(dateAStr) : null;
    const dateB = dateBStr ? dayjs(dateBStr) : null;
    
    if (dateA && dateB && dateA.isValid() && dateB.isValid()) {
      if (dateA.isBefore(dateB)) return -1;
      if (dateA.isAfter(dateB)) return 1;
    } else if (dateA && dateA.isValid()) {
      return -1;
    } else if (dateB && dateB.isValid()) {
      return 1;
    }
    return (a.pivot?.id || a.id || 0) - (b.pivot?.id || b.id || 0);
  });

  const best = sorted[0];
  return best.unit || best.complete_position_name || passenger.department || "";
};
